import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import api, { setAuthToken, clearAuthToken } from '../services/api';
import LocalAttendanceRepository from '../repositories/LocalAttendanceRepository';

const AuthContext = createContext(null);

function getStoredSession() {
  try {
    const raw = localStorage.getItem('biometric-auth');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function storeSession(user, token, refreshToken) {
  try {
    localStorage.setItem('biometric-auth', JSON.stringify({ user, token, refreshToken }));
    localStorage.setItem('auth_token', token);
  } catch {}
}

function clearStoredSession() {
  try {
    localStorage.removeItem('biometric-auth');
    localStorage.removeItem('auth_token');
  } catch {}
}

function guestTimeToIso(dateStr, timeStr) {
  if (!timeStr) return null;
  const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return null;
  let h = parseInt(match[1], 10);
  const m = match[2];
  const ampm = match[3].toUpperCase();
  if (ampm === 'PM' && h !== 12) h += 12;
  if (ampm === 'AM' && h === 12) h = 0;
  const hh = String(h).padStart(2, '0');
  // Guest times are wall-clock times in the visitor's own timezone, so build
  // the instant from a *local* date-time. Appending "Z" here would store 9:00 AM
  // in IST as 09:00 UTC (= 2:30 PM IST) once the cloud view converts it back.
  const local = new Date(`${dateStr}T${hh}:${m}:00`);
  return Number.isNaN(local.getTime()) ? null : local.toISOString();
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isGuest, setIsGuest] = useState(() => {
    try {
      return localStorage.getItem('biometric-guest') === 'true';
    } catch {
      return false;
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const localRepoRef = useRef(null);

  const getLocalRepo = useCallback(() => {
    if (!localRepoRef.current) {
      localRepoRef.current = new LocalAttendanceRepository();
    }
    return localRepoRef.current;
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    setIsGuest(false);
    clearAuthToken();
    clearStoredSession();
    try { localStorage.removeItem('biometric-guest'); } catch {}
  }, []);

  const restoreSessionRef = useRef(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;

    async function restoreSession() {
      const stored = getStoredSession();
      if (!stored || !stored.token) {
        if (mountedRef.current) setIsLoading(false);
        return;
      }

      setAuthToken(stored.token);

      try {
        const res = await api.get('/auth/me');
        if (!mountedRef.current) return;
        setUser(res.data.data);
        setToken(stored.token);
        localStorage.setItem('auth_token', stored.token);
        setIsGuest(false);
        try { localStorage.removeItem('biometric-guest'); } catch {}
      } catch {
        if (!mountedRef.current) return;
        clearAuthToken();
        clearStoredSession();
      } finally {
        if (mountedRef.current) setIsLoading(false);
      }
    }

    if (!restoreSessionRef.current) {
      restoreSessionRef.current = restoreSession().finally(() => {
        restoreSessionRef.current = null;
      });
    }

    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    const handleLogout = () => { logout(); };
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, [logout]);

  // Shared by every sign-in path (Google, email sign-in, email sign-up):
  // store the session, flip auth state and push any guest data to the account.
  const establishSession = useCallback(async (data) => {
    const { user: userData, accessToken, refreshToken } = data;
    setAuthToken(accessToken);
    storeSession(userData, accessToken, refreshToken);
    setUser(userData);
    setToken(accessToken);
    setIsGuest(false);
    try {
      localStorage.removeItem('biometric-guest');
    } catch {}

    // Build the repo on demand: after a page reload the guest's data is still
    // in localStorage, but nothing may have touched the repo yet.
    const localRepo = getLocalRepo();
    if (localRepo) {
      const allDays = localRepo.getAllDays();
      const dates = Object.keys(allDays).filter(Boolean);
      if (dates.length > 0) {
        const attendances = dates.map((date) => {
          const sessions = allDays[date] || [];
          const punches = [];
          for (const s of sessions) {
            const inTs = guestTimeToIso(date, s.inTime);
            if (inTs) punches.push({ type: 'IN', timestamp: inTs });
            const outTs = guestTimeToIso(date, s.outTime);
            if (outTs) punches.push({ type: 'OUT', timestamp: outTs });
          }
          return {
            date,
            expectedWorkingMinutes: localRepo.getExpectedWorkHours() * 60,
            punches,
          };
        });
        try {
          await api.post('/migration/import', { attendances });
        } catch (err) {
          console.warn('Guest data migration failed:', err);
        }
      }
    }
  }, [getLocalRepo]);

  const loginWithGoogle = useCallback(async (credentialResponse) => {
    try {
      setIsLoading(true);
      const res = await api.post('/auth/google', {
        idToken: credentialResponse.credential,
      });
      await establishSession(res.data.data);

      try {
        await api.post('/achievements/unlock/google_login');
      } catch {
        // achievement may already be unlocked
      }
    } catch (err) {
      console.error('Google login failed:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [establishSession]);

  // Email + password sign-in. Errors propagate so the form can show them;
  // we deliberately don't flip the global isLoading flag (it would unmount
  // the form mid-request via the route guards).
  const loginWithEmail = useCallback(async ({ email, password }) => {
    const res = await api.post('/auth/login', { email, password });
    await establishSession(res.data.data);
  }, [establishSession]);

  const registerWithEmail = useCallback(async ({ name, email, password }) => {
    const res = await api.post('/auth/register', { name, email, password });
    await establishSession(res.data.data);
  }, [establishSession]);

  // Always resolves for a well-formed request: the API answers identically
  // whether or not the email is registered (no account enumeration).
  const requestPasswordReset = useCallback(async (email) => {
    await api.post('/auth/forgot-password', { email });
  }, []);

  const resetPassword = useCallback(async (resetToken, password) => {
    await api.post('/auth/reset-password', { token: resetToken, password });
  }, []);

  // Replaces the password and swaps in the fresh session the API returns:
  // every other device is signed out, this one keeps working.
  const changePassword = useCallback(async ({ currentPassword, newPassword }) => {
    const res = await api.post('/auth/change-password', { currentPassword, newPassword });
    const { user: userData, accessToken, refreshToken } = res.data.data;
    setAuthToken(accessToken);
    storeSession(userData, accessToken, refreshToken);
    setUser(userData);
    setToken(accessToken);
  }, []);

  // Permanently deletes the account on the server, then drops the local session.
  const deleteAccount = useCallback(async ({ password } = {}) => {
    await api.post('/auth/delete-account', {
      confirmation: 'DELETE',
      ...(password ? { password } : {}),
    });
    logout();
  }, [logout]);

  const continueAsGuest = useCallback(() => {
    try {
      localStorage.setItem('biometric-guest', 'true');
    } catch {}
    setIsGuest(true);
    getLocalRepo();
  }, [getLocalRepo]);

  const switchToLogin = useCallback(() => {
    setIsGuest(false);
    try {
      localStorage.removeItem('biometric-guest');
    } catch {}
  }, []);

  const isAuthenticated = !!token && !!user;

  const value = {
    user,
    token,
    isAuthenticated,
    // A signed-in user is never a guest — even if a stale `biometric-guest`
    // flag lingers in localStorage (e.g. from tapping "Continue as Guest"
    // while a session existed). Auth always wins so the profile badge,
    // achievements page and dashboard reflect the real account.
    isGuest: isGuest && !isAuthenticated,
    isLoading,
    loginWithGoogle,
    loginWithEmail,
    registerWithEmail,
    requestPasswordReset,
    resetPassword,
    changePassword,
    deleteAccount,
    continueAsGuest,
    switchToLogin,
    logout,
    getLocalRepo,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export default AuthContext;
